/**
 * This constructor function is used to create a request table
 * @param  {object} options [request table options]
 * @return {object}         [returns an instance of the requestTable object]
 */
class requestTable {
	constructor(options) {
		this.container = document.querySelector( options.container );
		this.filterEnabled = typeof options.filterEnabled === "undefined" ? false : options.filterEnabled;
		this.chipFilter = options.chipFilter;
		this.table = {};
		this.tableHeaders = options.tableHeaders;
		this.tableActions = options.tableActions;
		this.tableData = options.tableData;
		this.modifiedTableData = options.tableData;
		this.panelData = options.panelData;
		this.sort = {
			sortField: null,
			sortDirection: 'desc'
		}
	}

	setTableData(data) {
		this.tableData = data;
		this.modifiedTableData = data;
		return this;
	}

	setPanelData(data) {
		this.panelData = data;
		return this;
	}

	/**
	 * This function is used to build table filters if filters are enabled
	 * The filters are built using the Mercury Forms Library
	 * @param  {string} filter1 [string representing the CSS selector for the fields filter]
	 * @param  {string} filter2 [string representing the CSS selector for the string filter]
	 * @param  {string} filter3 [string representing the CSS selector for the date filter]
	 * @param  {string} filter4 [string representing the CSS selector for the number filter]
	 */
	buildFilters(filter1, filter2, filter3, filter4) {
		const self = this;
		let $formChipBag;

		// Create the fields filter
		// The fields filter controls which table field the user is filtering on
		// The requestTable is smart enough to detect the field type for the selected fields
		// If a string field is selected, the text input is shown
		// If a date field is selected, the date input is shown
		// If a numeric field is selected, the number input is shown
		let $fieldsFilter = new Dropdown({
			container: filter1,
			elementExists: true,
			onChange: function() {
				// Determine the data type for the selected field
				let fieldTypeCheck = self.tableData[0][$fieldsFilter.val()];

				// Reset the value for the string filter on change
				$stringFilter
					.setPlaceholder("Type a Value")
					.val(null);

				// Reset the value for the number filter on change
				$numberFilter
					.setPlaceholder("Type a Value")
					.val(null);

				// Based on the data type of the selected field, show the appropriate filter
				if ( fieldTypeCheck instanceof Date ) {
					$stringFilter.container.style.display = "none";
					$dateFilter.container.style.display = "";
					$numberFilter.container.style.display = "none";
				} else if ( typeof fieldTypeCheck === "number" ) {
					$stringFilter.container.style.display = "none";
					$dateFilter.container.style.display = "none";
					$numberFilter.container.style.display = "";
				} else {
					$stringFilter.container.style.display = "";
					$dateFilter.container.style.display = "none";
					$numberFilter.container.style.display = "none";
				}

				// if chip filters are not selected, reset the requestTable and show all rows
				if ( $formChipBag.activeChipsValues.length === 0 ) {
					self.modifiedTableData = self.tableData;
					self.clearTableBody();
					self.buildTableBody(self.modifiedTableData);
					self.rebindTableBodyEvents();
				}
			}
		});

		// Configure the string filter
		let $stringFilter = new textInput({
			container: filter2,
			elementExists: true,
			onKeyup: function() {
				if ( typeof self.chipFilter !== "undefined" ) {
					self.filterTable($fieldsFilter.val(), $stringFilter.val(), self.chipFilter.filterField, $formChipBag.activeChipsValues);
				} else {
					self.filterTable($fieldsFilter.val(), $stringFilter.val(), );
				}
			}
		});

		// Configure the date filter
		let $dateFilter = new datePicker({
			container: filter3,
			elementExists: true,
			onChange: function() {
				if ( typeof self.chipFilter !== "undefined" ) {
					self.filterTable($fieldsFilter.val(), $dateFilter.getDate(), self.chipFilter.filterField, $formChipBag.activeChipsValues);
				} else {
					self.filterTable($fieldsFilter.val(), $dateFilter.getDate());
				}
			},
			onKeyup: function() {
				if ( typeof self.chipFilter !== "undefined" ) {
					self.filterTable($fieldsFilter.val(), $dateFilter.getDate(), self.chipFilter.filterField, $formChipBag.activeChipsValues);
				} else {
					self.filterTable($fieldsFilter.val(), $dateFilter.getDate());
				}
			}
		});

		// Configure the number filter
		let $numberFilter = new textInput({
			container: filter4,
			elementExists: true,
			onKeyup: function() {
				if ( typeof self.chipFilter !== "undefined" ) {
					self.filterTable($fieldsFilter.val(), $numberFilter.val(), self.chipFilter.filterField, $formChipBag.activeChipsValues);
				} else {
					self.filterTable($fieldsFilter.val(), $numberFilter.val());
				}
			}
		});

		// If a chip filter is enabled, build it
		if ( typeof this.chipFilter !== "undefined" ) {
			// grab the field to filter on with the chip filter
			const filterField = this.chipFilter.filterField;
			$formChipBag = new chipBag({
				container: ".chips-wrapper",
				elementExists: true,
				onClick: () => {
					if ( $stringFilter.container.style.display === "" ) {
						this.filterTable(filterField, $formChipBag.activeChipsValues, $fieldsFilter.val(), $stringFilter.val());
					} else if ( $numberFilter.container.style.display === "" ) {
						this.filterTable(filterField, $formChipBag.activeChipsValues, $fieldsFilter.val(), $numberFilter.val());
					} else if ( $dateFilter.container.style.display === "" ) {
						this.filterTable(filterField, $formChipBag.activeChipsValues, $fieldsFilter.val(), $dateFilter.getDate());
					}
				}
			});

			const chipsData = [...new Set(this.tableData.map((chip) => chip[filterField]))]
				.map( (chip, index) => {
					return {
						value: chip,
						display: chip
					}
				})
			$formChipBag.data = chipsData;
			$formChipBag.buildChips();
			this.chipFilter.chipBag = $formChipBag;
		}

		$dateFilter.setDate();
		$dateFilter.container.style.display = "none";
		$numberFilter.container.style.display = "none";

		$fieldsFilter.data = Object.keys(this.tableHeaders)
			.map((field) => {
				return {
					list_value: field,
					list_display: self.tableHeaders[field]
				}
			})
		$fieldsFilter.buildDropdown();

	} // buildFilters();

	buildTableHeaders() {
		if ( typeof this.tableHeaders === "undefined" ) {
			throw "Please define tableHeaders option";
		}

		const tableHeaders = this.tableHeaders;
		const tableActions = this.tableActions;

		let table = document.createElement("table");
		table.classList.add('material-table');

		let thead = document.createElement("thead");
		let theadRow = document.createElement("tr");

		for (let key in tableHeaders) {
			let th = document.createElement("th");
			th.setAttribute("data-field-name", key);
			th.textContent = tableHeaders[key];
			theadRow.appendChild(th);
		}

		tableActions.forEach(function(v) {
			if ( v.includeInHeader ) {
				let th = document.createElement("th");
				let i = document.createElement("i");
				i.classList.add("material-icons");

				if ( v.action === "view" ) {
					i.textContent = "visibility";
					i.classList += " view";
				} else	if ( v.action === "edit" ) {
					i.textContent = "mode_edit";
					i.classList += " edit";
				} else if ( v.action === "email" ) {
					i.textContent = "email";
					i.classList += " email";
				} else if ( v.action === "delete" ) {
					i.textContent = "delete_forever";
					i.classList += " delete text-red";
				}

				th.appendChild(i);

				if ( v.position === "start" ) {
					theadRow.insertBefore(th, theadRow.firstChild);
				} else if ( v.position === "end" ) {
					theadRow.appendChild(th);
				}
			} else {
				let th = document.createElement("th");
				if ( v.position === "start" ) {
					theadRow.insertBefore(th, theadRow.firstChild);
				} else if ( v.position === "end" ) {
					theadRow.appendChild(th);
				}
			}
		})

		thead.appendChild(theadRow);
		table.appendChild(thead);
		this.container.appendChild(table);
		return this;
	}

	buildTableBody(data) {
		if ( typeof this.tableData === "undefined" ) {
			throw "Please define table data";
		}

		const self = this;
		const tableActions = this.tableActions;
		let tableData = [];
		if ( typeof data === "undefined" ) {
			tableData = this.tableData;
		} else {
			tableData = data;
		}

		let table = this.container.querySelector("table");
		let tbody = document.createElement("tbody");

		tableData.forEach(function(v,k) {
			let tr = document.createElement("tr");
			tr.setAttribute("data-index", k);

			for ( let key in v ) {
				if ( self.tableHeaders[key] ) {
					let td = document.createElement("td");

					// this allows us to maintain the original date as a date but display it as a formatted string
					if ( v[key] instanceof Date ) {
						td.innerHTML = formatDate(v[key]);
					} else {
						td.innerHTML = v[key];
					}
					tr.appendChild(td);
				}
			}
			tbody.appendChild(tr);

			tableActions.forEach(function(v) {
				let td = document.createElement("td");
				let i = document.createElement("i");
				i.classList.add("material-icons");

				if ( v.action === "view" ) {
					i.textContent = v.icon || "visibility";
					i.classList += " view text-blue";
				} else if ( v.action === "edit" ) {
					i.textContent = v.icon || "mode_edit";
					i.classList += " edit";
				} else if ( v.action === "email" ) {
					i.textContent = v.icon || "email";
					i.classList += " email";
				} else if ( v.action === "delete" ) {
					i.textContent = v.icon || "delete_forever";
					i.classList += " delete text-red";
				}

				td.appendChild(i);
				if ( v.position === "start" ) {
					tr.insertBefore(td, tr.firstChild);
				} else if ( v.position === "end" ) {
					tr.appendChild(td);
				}
			})
		})

		table.appendChild(tbody);
		return this;
	}

	clearTableBody() {
		let tbody = this.container.querySelector("table tbody");
		tbody.remove();
	}

	buildTable() {
		this.buildTableHeaders();
		this.buildTableBody();
		this.table = this.container.querySelector("table");
		this.bindEvents();
		return this;
	}

	buildPanel(e, eventHandler) {
		const table = e.closest("table");
		const existingPanel = table.querySelector(".material-table-edit-card");
		if ( existingPanel !== null ) {
			existingPanel.remove();
		}

		const tr = e.closest("tr");
		const trs = table.querySelectorAll("tr");
		const index = tr.rowIndex;

		trs.forEach(function(v) {
			v.classList.remove("active");
		})

		tr.classList.add("active");

		const panel = table.insertRow(index+1);
		panel.classList.add("material-table-edit-card");

		const colCount = Object.keys(this.tableHeaders).length + this.tableActions.length;
		let td = document.createElement("td");
		td.setAttribute("colspan", colCount);

		let cardContent = document.createElement("div");
		cardContent.classList.add("card-content");
		cardContent.classList.add("row");

		td.appendChild(cardContent);
		panel.appendChild(td);
		eventHandler(e, cardContent);
	}

	hidePanel() {
		const table = this.container.querySelector("table");
		const trs = table.querySelectorAll("tr");
		const panel = table.querySelector(".material-table-edit-card");
		panel.remove();

		trs.forEach(function(v) {
			v.classList.remove("active");
		})
		return this;
	}

	sortTable(e) {
		const sortDirection = this.sort.sortDirection,
					self = this;

		let target = e.target,
				sortField = '';

		const sortAscending = (a,b) => {
			if (a[sortField] > b[sortField])
				return -1;
			if (a[sortField] < b[sortField])
				return 1;
			return 0;
		}

		const sortDescending = (a,b) => {
			if (a[sortField] < b[sortField])
				return -1;
			if (a[sortField] > b[sortField])
				return 1;
			return 0;
		}

		if ( target.tagName.toLowerCase() === "th" ) {
			target = e.target;
		  sortField = target.getAttribute("data-field-name");
		} else if ( target.tagName.toLowerCase() === "i" ) {
			target = e.target.parentNode;
			sortField = target.getAttribute("data-field-name");
		}

		let existingSortField = this.sort.sortField;

		if ( existingSortField === null ) {
			existingSortField = sortField;
		}

		this.sort.sortField = sortField;
		if ( existingSortField === sortField ) {
			if ( sortDirection === "asc" ) {
				this.sort.sortDirection = "desc";
				this.modifiedTableData = this.modifiedTableData.sort(sortAscending);
			} else {
				this.sort.sortDirection = "asc";
				this.modifiedTableData = this.modifiedTableData.sort(sortDescending);
			}
		} else {
			this.sort.sortDirection = "asc";
			this.modifiedTableData = this.modifiedTableData.sort(sortDescending);
		}

		this.clearTableBody();
		this.buildTableBody(this.modifiedTableData);
		this.rebindTableBodyEvents();
		this.table = this.container.querySelector("table");

		const ths = this.container.querySelectorAll("th");
		ths.forEach(function(v) {
			const fieldName = v.getAttribute("data-field-name");
			v.textContent = self.tableHeaders[fieldName];
		})

		if ( this.sort.sortDirection === "desc") {
			target.innerHTML = `<i class="material-icons text-blue vertical-align-middle margin-right">sort</i> ${this.tableHeaders[sortField]}` ;
		} else {
			target.innerHTML = `<i class="material-icons text-blue vertical-align-middle text-rotate-180 margin-right">sort</i> ${this.tableHeaders[sortField]}` ;
		}
		return this;
	}

	filterTable(filterField, filterValue, secondFilterField, secondFilterValue) {
		const self = this;
		if ( typeof this.chipFilter !== "undefined" ) {
			let fieldTypeCheck = self.tableData[0][filterField];
			this.modifiedTableData = this.tableData
				.filter( data => {
					if ( fieldTypeCheck instanceof Date ) {
						if ( secondFilterValue.length !== 0 ) {
							console.log('a');
							return formatDate(data[filterField]) === formatDate(reStructureDate(filterValue)) && secondFilterValue.includes(data[secondFilterField]);
						} else {
							console.log('b');
							return formatDate(data[filterField]) === formatDate(reStructureDate(filterValue));
						}
					} else if ( typeof fieldTypeCheck === "number" ) {
						if ( secondFilterValue.length !== 0 ) {
							return data[filterField].toString().includes(filterValue) && secondFilterValue.includes(data[secondFilterField]);
						} else {
							return data[filterField].toString().includes(filterValue);
						}
					} else if ( typeof filterValue === "object" ) {
						let secondFieldTypeCheck = self.tableData[0][secondFilterField];
						if ( filterValue.length === 0  && secondFilterValue !== "" ) {
							if ( secondFieldTypeCheck instanceof Date ) {
								return formatDate(data[secondFilterField]) === formatDate(reStructureDate(secondFilterValue));
							} else if ( typeof secondFieldTypeCheck === "number" ) {
								return data[secondFilterField].toString().includes(secondFilterValue);
							} else {
								return data[secondFilterField].toLowerCase().includes(secondFilterValue.toLowerCase());
							}
						} else if ( filterValue.length === 0 && secondFilterValue === "" ) {
							return data;
						} else if ( secondFilterValue !== "" ) {
							if ( secondFieldTypeCheck instanceof Date ) {
								return filterValue.includes(data[filterField]) && formatDate(data[secondFilterField]) === formatDate(reStructureDate(secondFilterValue));
							} else if ( typeof secondFieldTypeCheck === "number" ) {
								return filterValue.includes(data[filterField]) && data[secondFilterField].toString().includes(secondFilterValue);
							} else {
								return filterValue.includes(data[filterField]) && data[secondFilterField].toLowerCase().includes(secondFilterValue.toLowerCase());
							}
						} else {
							return filterValue.includes(data[filterField]);
						}
					} else if ( typeof filterValue === "string" ) {
						if ( secondFilterValue.length !== 0 ) {
							return data[filterField].toLowerCase().includes(filterValue.toLowerCase()) && secondFilterValue.includes(data[secondFilterField]);
						} else {
							return data[filterField].toLowerCase().includes(filterValue.toLowerCase());
						}
					}
				});
		} else {
			this.modifiedTableData = this.tableData
				.filter((data) => {
					let fieldTypeCheck = self.tableData[0][filterField];
					if ( fieldTypeCheck instanceof Date ) {
						return formatDate(data[filterField]) === formatDate(reStructureDate(filterValue));
					} else if ( typeof fieldTypeCheck === "number" ) {
						return data[filterField].toString().includes(filterValue)
					} else if ( typeof filterValue === "object" ) {
						if ( filterValue.length === 0 ) {
							return data;
						} else {
							return filterValue.includes(data[filterField]);
						}
					} else {
						return data[filterField].toLowerCase().includes(filterValue.toLowerCase())
					}
				});
		}
		this.clearTableBody();
		this.buildTableBody(this.modifiedTableData);
		this.rebindTableBodyEvents();
	}

	rebindTableBodyEvents() {
		const self = this;
		let $viewBtns = this.container.querySelectorAll("i.view");
		let $deleteBtns = this.container.querySelectorAll("i.delete");
		$viewBtns.forEach(function(v) {
			v.addEventListener("click", self.buildPanel.bind(self, v, self.tableActions[0].eventHandler));
		})
		$deleteBtns.forEach(function(v) {
			v.addEventListener("click", self.buildPanel.bind(self, v, self.tableActions[1].eventHandler));
		})
		return this;
	}

	bindEvents() {
		const self = this;
		let $th = this.container.querySelectorAll("th");
		let $viewBtns = this.container.querySelectorAll("i.view");
		let $deleteBtns = this.container.querySelectorAll("i.delete");
		$th.forEach(function(v) {
			v.addEventListener("click", self.sortTable.bind(self));
		})
		$viewBtns.forEach(function(v) {
			v.addEventListener("click", self.buildPanel.bind(self, v, self.tableActions[0].eventHandler));
		})
		$deleteBtns.forEach(function(v) {
			v.addEventListener("click", self.buildPanel.bind(self, v, self.tableActions[1].eventHandler));
		})
		return this;
	}
}

const reStructureDate = (date) => {
	const dateSplit = date.split("-"),
				month = dateSplit[1],
				day = dateSplit[2],
				year = dateSplit[0];

	return `${month}/${day}/${year}`;
}

/**
 * This function is used to return a date formatted in "MM/DD/YYY"
 * @param  {string} date [string representation of date]
 * @return {date}      [formatted date in "MM/DD/YYYY"]
 */
const formatDate = (date) => {
	let newDate = new Date(date);
	let formatedDate = `${newDate.getMonth()+1}/${newDate.getDate()}/${newDate.getFullYear()}`;
	return formatedDate;
}

const addClass = (element,className) => {
	if ( !element.classList.contains(className) ) {
		element.classList.add(className);
	}
}

const removeClass = (element,className) => {
	if ( element.classList.contains(className) ) {
		element.classList.remove(className);
	}
}

const createFormGroup = (labelText, inline) => {
	const $formGroup = document.createElement("div");
	addClass($formGroup,"form-group");
	if ( inline === "inline" ) {
		addClass($formGroup,"form-group-inline");
	}
	addClass($formGroup,"margin-top");

	if ( labelText ) {
		const $label = document.createElement("label");
		$label.textContent = labelText;
		$formGroup.appendChild($label);
	}
	return $formGroup;
}
